import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Badge,
  Card,
  CardHeader,
  CardBody,
  SimpleGrid,
  Icon,
  Divider,
} from '@chakra-ui/react';
import { WarningIcon, CheckCircleIcon, InfoOutlineIcon } from '@chakra-ui/icons';

const denialData = [
  {
    carc: 'CARC 97',
    title: 'Payment Included in Another Service',
    context: 'Same-Day Visit Restrictions',
    description: 'Medi-Cal generally limits FQHCs to one Prospective Payment System (PPS) encounter per patient, per day. If a patient has a medical visit and a subsequent visit, the second claim will deny with CARC 97 unless properly justified.',
    revedySolution: 'Revedy automatically detects same-day encounters, reads both clinical notes to verify a distinct illness or injury, appends Modifier 59, and drafts the required Box 80 (Remarks) justification text.',
    colorScheme: 'red'
  },
  {
    carc: 'CARC 16',
    title: 'Lacks Information / Billing Error',
    context: 'Missing Mental Health Modifiers',
    description: 'When billing a mental health encounter (Rev 0900 + T1015), Medi-Cal requires a specific modifier indicating the provider\'s credential level (e.g., AH for Clinical Psychologist, AJ for Clinical Social Worker). Missing this causes an immediate CARC 16 denial.',
    revedySolution: 'Revedy cross-references the rendering provider\'s NPI and credential against the Medi-Cal Psychological Services manual and auto-appends the correct modifier (AH, AJ, HN) prior to submission.',
    colorScheme: 'orange'
  },
  {
    carc: 'CARC 22',
    title: 'Covered by Another Payer (COB)',
    context: 'Improper Managed Care Wrap',
    description: 'Occurs when an FQHC bills Medi-Cal directly for a patient with a Medicare Advantage or Commercial HMO primary, or fails to format the secondary "wrap" claim correctly.',
    revedySolution: 'Revedy detects the primary payer EOB, calculates the exact wrap amount owed, and auto-generates the secondary claim using Rev 0521, T1015, and the mandatory SE modifier.',
    colorScheme: 'blue'
  },
  {
    carc: 'CARC 50',
    title: 'Medical Necessity Not Met',
    context: 'Unsupported Procedure Codes',
    description: 'The billed ICD-10 diagnosis code does not support the CPT procedure code according to DHCS Local Coverage Determinations (LCD) or specific Medi-Cal policy.',
    revedySolution: 'The Revedy LLM reads the full clinical note, identifies a documented but unbilled supporting diagnosis (e.g., Diabetic Neuropathy), and proposes adding it to the claim to satisfy medical necessity.',
    colorScheme: 'purple'
  }
];

const FqDemoDenialIntelligence = () => {
  return (
    <Container maxW="container.xl" py={8}>
      <VStack align="stretch" spacing={6}>
        <Box>
          <Heading size="lg" color="brand.700" mb={2}>Medi-Cal FQHC Denial Intelligence</Heading>
          <Text color="gray.600">
            Demonstrating Revedy's deep understanding of common Claim Adjustment Reason Codes (CARC) specific to California FQHCs, and how our intelligence layer automatically prevents or resolves them.
          </Text>
        </Box>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
          {denialData.map((denial, idx) => (
            <Card key={idx} variant="outline" borderColor={`${denial.colorScheme}.200`} bg="white">
              <CardHeader bg={`${denial.colorScheme}.50`} pb={3}>
                <HStack justify="space-between" align="start">
                  <VStack align="start" spacing={1}>
                    <Badge colorScheme={denial.colorScheme} fontSize="md" px={2} py={1} borderRadius="md">
                      {denial.carc}
                    </Badge>
                    <Heading size="sm" color={`${denial.colorScheme}.700`}>{denial.title}</Heading>
                  </VStack>
                  <Badge variant="outline" colorScheme={denial.colorScheme}>
                    {denial.context}
                  </Badge>
                </HStack>
              </CardHeader>
              <CardBody>
                <VStack align="stretch" spacing={4}>
                  <Box>
                    <HStack mb={2}>
                      <Icon as={WarningIcon} color="gray.500" />
                      <Text fontWeight="bold" fontSize="sm" color="gray.700">The Medi-Cal Challenge</Text>
                    </HStack>
                    <Text fontSize="sm" color="gray.600">
                      {denial.description}
                    </Text>
                  </Box>
                  <Divider />
                  <Box bg="green.50" p={3} borderRadius="md" borderWidth="1px" borderColor="green.200">
                    <HStack mb={2} align="start">
                      <Icon as={CheckCircleIcon} color="green.500" mt={1} />
                      <Box>
                        <Text fontWeight="bold" fontSize="sm" color="green.800">The Revedy Solution</Text>
                        <Text fontSize="sm" color="green.700" mt={1}>
                          {denial.revedySolution}
                        </Text>
                      </Box>
                    </HStack>
                  </Box>
                </VStack>
              </CardBody>
            </Card>
          ))}
        </SimpleGrid>
      </VStack>
    </Container>
  );
};

export default FqDemoDenialIntelligence;
